import { type AppErrorCode, isAppError } from "@shared/app-error";
import { reportToSentry } from "./sentry";

/**
 * §5.8 の集約 catch。 AppError.code を switch で網羅し、ユーザー通知/フォールバック/
 * スキップのいずれを行うかは policy ではなく code 単位で決める (同じコードでも
 * 文脈で policy が変わる余地があるため、判断ロジックは呼び出し側 entry point に置く)。
 *
 * 実際の通知 UI (トースト・モーダル) は M7 の通知サーフェス整備時に差し込む。
 * 現段階ではコンソールへの記録 + Sentry 送信に留める。
 */

type Notifier = (message: string) => void;

let notifier: Notifier = (message) => {
  console.error("[app-error]", message);
};

export function setAppErrorNotifier(next: Notifier): void {
  notifier = next;
}

export function handleAppError(err: unknown): void {
  if (!isAppError(err)) {
    // 開発時のデバッグと、 Sentry 不在時 (DSN 未設定) でもログから原因を辿れるようにする。
    // PII 候補が出るのは Sentry 同様に注意するが、 console は開発者ツールでのみ可視。
    console.error("[handleAppError] non-AppError:", err);
    reportToSentry(err);
    notifier("予期しないエラーが発生しました");
    return;
  }

  console.error("[handleAppError]", err.code, err.message, (err as { cause?: unknown }).cause);
  reportToSentry(err, { tags: { errorCode: err.code, errorPolicy: err.policy } });
  notifier(messageFor(err.code, err.message));
}

function messageFor(code: AppErrorCode, fallback: string): string {
  switch (code) {
    case "CameraDenied":
      return "カメラの利用を許可してください";
    case "CameraStreamLost":
      return "カメラの接続が切れました";
    case "MediaPipeInitFailed":
      return "顔検出エンジンの初期化に失敗しました";
    case "MediaPipeRuntimeError":
      return "顔検出エンジンでエラーが発生しました";
    case "FrameDropped":
      return "フレームの解析に失敗しました";
    case "IndexedDBQuotaExceeded":
      return "ストレージ容量が不足しています";
    case "IndexedDBUnavailable":
      return "ローカル DB が利用できないため、メモリで継続します";
    case "IndexedDBError":
      return "ローカル DB の書き込みに失敗しました";
    case "WorkerRpcInFlight":
      return "解析処理の状態が不整合になりました";
    case "PipUnsupported":
      return "Picture-in-Picture が利用できません";
    case "PipRequestFailed":
      return "Picture-in-Picture の起動に失敗しました";
    case "PipStylesheetNoRealm":
      return "PiP Window のスタイル共有に失敗しました";
    default:
      // "Unexpected" と dev-only feature (issue #76 検知施策評価基盤) の AppErrorCode は
      // default fallback で処理する。 case label として書かないため string literal が
      // production bundle に残らない。 AppErrorCode 自体は app-error.ts の type-only export
      // なので bundle 影響なし。
      return fallback || "予期しないエラーが発生しました";
  }
}
